import { useCallback } from 'react';

const DB_NAME = 'ugpp_documentos_db';
const STORE_NAME = 'documentos';
const DB_VERSION = 1;

interface DocRecord {
  codigo: string;
  nombre: string;
  tipo: string;
  data: ArrayBuffer;
  fecha: string;
}

const openDB = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME, { keyPath: 'codigo' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

export function useDocumentosDB() {
  const saveDocumento = useCallback(async (codigo: string, file: File): Promise<void> => {
    const db = await openDB();
    const arrayBuffer = await file.arrayBuffer();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put({
        codigo,
        nombre: file.name,
        tipo: file.type || 'application/octet-stream',
        data: arrayBuffer,
        fecha: new Date().toISOString(),
      } as DocRecord);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }, []);

  const getDocumento = useCallback(async (codigo: string): Promise<DocRecord | null> => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(codigo);
      req.onsuccess = () => resolve((req.result as DocRecord) ?? null);
      req.onerror = () => reject(req.error);
    });
  }, []);

  const downloadDocumento = useCallback(
    async (codigo: string): Promise<void> => {
      const doc = await getDocumento(codigo);
      if (!doc) return;
      const blob = new Blob([doc.data], { type: doc.tipo });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.nombre;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    [getDocumento],
  );

  const hasDocumento = useCallback(
    async (codigo: string): Promise<boolean> => {
      const doc = await getDocumento(codigo);
      return doc !== null;
    },
    [getDocumento],
  );

  return { saveDocumento, getDocumento, downloadDocumento, hasDocumento };
}
